import { useCallback, useEffect, useRef, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { espelharLojaPublica } from "../../../utils/lojaPublica";

/* paymentConfig do vendedor (users/{uid}.paymentConfig, espelhado na loja
   pública) com salvamento automático:
   - cada alteração é aplicada na tela na hora e salva em FILA, na ordem;
   - se o salvamento falhar, a tela volta ao último estado salvo e o erro sai
     em `estado` ("erro"), sem deixar valor "meio salvo";
   - `salvar(patch)` devolve uma promessa: formulários em janela (chave PIX,
     maquininhas) esperam o resultado antes de fechar.
   O formato do documento não muda — PDV, Pedidos, Vendas online e a
   calculadora de cartão leem os mesmos campos. */

export const CFG_VAZIO = {
  pixEnabled: false, pixChave: "", pixBeneficiario: "", pixCidade: "",
  maquininhas: null, maquininhaPadraoId: "",
};

export function useConfigPagamento(uid) {
  const [cfg, setCfg] = useState(null);
  const [estado, setEstado] = useState("ocioso"); // ocioso · salvando · salvo · erro
  const loja = useRef({});
  const confirmado = useRef(null);
  const atual = useRef(null);
  const fila = useRef(Promise.resolve());
  const pendentes = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    let vivo = true;
    getDoc(doc(db, "users", uid))
      .then(snap => {
        const d = snap.exists() ? snap.data() : {};
        loja.current = { empresa: d.empresa, nome: d.nome, logoUrl: d.logoUrl, email: d.email };
        const inicial = { ...CFG_VAZIO, ...(d.paymentConfig || {}) };
        confirmado.current = inicial;
        atual.current = inicial;
        if (vivo) setCfg(inicial);
      })
      .catch(() => {
        confirmado.current = CFG_VAZIO;
        atual.current = CFG_VAZIO;
        if (vivo) { setCfg(CFG_VAZIO); setEstado("erro"); }
      });
    return () => { vivo = false; clearTimeout(timer.current); };
  }, [uid]);

  const salvar = useCallback(patch => {
    const novo = { ...atual.current, ...patch };
    atual.current = novo;
    setCfg(novo);
    setEstado("salvando");
    clearTimeout(timer.current);
    pendentes.current += 1;
    const tarefa = fila.current.then(async () => {
      try {
        await updateDoc(doc(db, "users", uid), { paymentConfig: novo });
        await espelharLojaPublica(uid, { ...loja.current, paymentConfig: novo });
        confirmado.current = novo;
        return true;
      } catch {
        // Volta ao último estado que o servidor tem
        atual.current = confirmado.current;
        setCfg(confirmado.current);
        return false;
      }
    });
    fila.current = tarefa.catch(() => false);
    return tarefa.then(ok => {
      pendentes.current -= 1;
      if (!ok) { setEstado("erro"); return false; }
      if (pendentes.current === 0) {
        setEstado("salvo");
        timer.current = setTimeout(() => setEstado("ocioso"), 2600);
      }
      return true;
    });
  }, [uid]);

  return { cfg, salvar, estado, carregando: cfg === null };
}
